import { AUTH_MESSAGES, loginInput, type SecondFactorChallenge } from '@fos/shared/contracts/identity';
import { type FormEvent, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { safeNext } from '../../../app/guards';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useLogin, useLogout, useSession } from '../../../lib/auth';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Field } from '../../../ui/Field';
import { Input, PasswordInput } from '../../../ui/Input';
import { AuthLayout } from '../components/AuthLayout';
import { SecondFactorStep } from '../components/SecondFactorStep';

type FieldErrors = Partial<Record<'email' | 'password', string>>;

/**
 * Logowanie (docs/API_AUTH.md): email + hasło, a gdy konto wymaga drugiego składnika (Fala 7) — krok „Wpisz kod
 * z maila”. Sesja czekająca na kod (np. po odświeżeniu strony) od razu pokazuje krok kodu.
 */
export function LoginPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const logout = useLogout();
  const session = useSession();
  const [challenge, setChallenge] = useState<SecondFactorChallenge | null>(null);
  const pendingChallenge =
    session.data?.authenticated && session.data.stage === 'second_factor_required'
      ? (session.data.secondFactor ?? null)
      : null;
  const codeStep = challenge ?? pendingChallenge;

  const [email, setEmail] = useState(() => (location.state as { email?: string } | null)?.email ?? '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);

  const notice = (location.state as { notice?: string } | null)?.notice;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsed = loginInput.safeParse({ email, password, remember });
    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if ((key === 'email' || key === 'password') && !errors[key]) {
          errors[key] = key === 'email' && !email.trim() ? 'Podaj adres email' : issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    try {
      const res = await login.mutateAsync(parsed.data);
      if (res.stage === 'second_factor_required' && res.secondFactor) {
        setPassword('');
        setChallenge(res.secondFactor);
        return;
      }
      navigate(safeNext(params.get('next')), { replace: true });
    } catch (err) {
      setPassword('');
      const code = appErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const issues = validationIssues(err);
        const errors: FieldErrors = {};
        for (const issue of issues) {
          if (issue.path === 'email' || issue.path === 'password') errors[issue.path] = issue.message;
        }
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          return;
        }
      }
      setFormError(code === 'UNAUTHENTICATED' ? AUTH_MESSAGES.invalidCredentials : errorMessage(err));
    }
  };

  const restart = (message?: string) => {
    // sesja czekająca na kod przestaje istnieć (wylogowanie), wracamy do hasła
    logout.mutate();
    setChallenge(null);
    setPassword('');
    setFormError(message ?? null);
  };

  if (codeStep) {
    return (
      <AuthLayout title="Wpisz kod z maila" description="Drugi krok logowania.">
        <SecondFactorStep
          challenge={codeStep}
          onVerified={() => navigate(safeNext(params.get('next')), { replace: true })}
          onRestart={restart}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Zaloguj się" description="Witaj w Fravity OS.">
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Logowanie">
        {notice === 'passwordChanged' && !formError && (
          <FormMessage tone="success">Hasło zmienione. Zaloguj się nowym hasłem.</FormMessage>
        )}
        <Field label="Email" error={fieldErrors.email}>
          <Input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field
          label="Hasło"
          error={fieldErrors.password}
          labelAction={
            <Link
              to="/nie-pamietam-hasla"
              state={{ email: email.trim() }}
              className="rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              Nie pamiętasz hasła?
            </Link>
          }
        >
          <PasswordInput
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Checkbox checked={remember} onCheckedChange={setRemember} label="Zapamiętaj mnie na 30 dni" />
        {formError && <FormMessage>{formError}</FormMessage>}
        <Button type="submit" variant="primary" size="l" fullWidth loading={login.isPending} className="mt-1">
          Zaloguj
        </Button>
      </form>
    </AuthLayout>
  );
}
